import type { CashCountResult, MoneyView } from '../screens/CashSession.js'
import type { CreatedOrderView, ConfirmedOrderView } from '../screens/NewOrder.js'

/**
 * The server's answers to the counter's own writes, mapped rather than cast.
 *
 * `src/api/reads.ts` exists because four screens guessed the shape of a read and all four
 * typechecked and crashed (`PROGRESS.md` problem 109). A write's answer crosses the same wire with
 * the same `JSON.parse` at the end of it, and `QueuedWrite.response` is `unknown` precisely so that
 * nobody casts it: these are the mappers that turn it into the view types the screens declare.
 *
 * Every shape below was taken from the running API, not from the service's TypeScript. The two
 * differ in one place already, and that place matters: `POST /cash-sessions/count` answers with
 * `zReport.sessionId` and `zReport.terminalId` alongside the money, which `ZReportView` does not
 * declare and this mapper therefore drops rather than passing through.
 *
 * A missing field throws with the field named. The alternative is a screen rendering `undefined` as
 * an amount, which at a till is a figure somebody acts on.
 */

export class WriteResultMismatch extends Error {
  constructor(readonly field: string) {
    super(
      `The server's answer is missing ${field}. This is a version mismatch between the counter and ` +
        `the API, not something you did. The write itself was accepted.`,
    )
    this.name = 'WriteResultMismatch'
  }
}

const record = (value: unknown, field: string): Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new WriteResultMismatch(field)
  }
  return value as Record<string, unknown>
}

const money = (value: unknown, field: string): MoneyView => {
  const shape = record(value, field)
  const minor = shape['minor']
  const currency = shape['currency']
  if (typeof minor !== 'string') throw new WriteResultMismatch(`${field}.minor`)
  if (typeof currency !== 'string') throw new WriteResultMismatch(`${field}.currency`)
  // `MR-01`: minor units and a currency, together, never one without the other.
  return { minor, currency }
}

const text = (value: unknown, field: string): string => {
  if (typeof value !== 'string' || value === '') throw new WriteResultMismatch(field)
  return value
}

const whole = (value: unknown, field: string): number => {
  if (typeof value !== 'number' || !Number.isInteger(value)) throw new WriteResultMismatch(field)
  return value
}

/** `POST /orders`. The draft, and the id the confirm step cannot be reached without. */
export function toCreatedOrder(response: unknown): CreatedOrderView {
  const body = record(response, 'the created order')
  const lines = body['lines']
  if (!Array.isArray(lines)) throw new WriteResultMismatch('lines')
  return {
    id: text(body['id'], 'id'),
    subtotal: money(body['subtotal'], 'subtotal'),
    tax: money(body['tax'], 'tax'),
    total: money(body['total'], 'total'),
    orderDiscount: money(body['orderDiscount'], 'orderDiscount'),
    lines: lines.map((line, index) => {
      const shape = record(line, `line ${String(index + 1)}`)
      return {
        id: text(shape['id'], `line ${String(index + 1)} id`),
        position: whole(shape['position'], `line ${String(index + 1)} position`),
        description: text(shape['description'], `line ${String(index + 1)} description`),
        net: money(shape['net'], `line ${String(index + 1)} net`),
        tax: money(shape['tax'], `line ${String(index + 1)} tax`),
        gross: money(shape['gross'], `line ${String(index + 1)} gross`),
        // `MR-05`: the order discount apportioned back, so per-line margin stays truthful.
        orderDiscountShare: money(
          shape['orderDiscountShare'],
          `line ${String(index + 1)} orderDiscountShare`,
        ),
      }
    }),
  }
}

/** `POST /orders/confirm`. `TAX-002`'s invoice number, out of `OFF-003`'s terminal block. */
export function toConfirmedOrder(response: unknown): ConfirmedOrderView {
  const body = record(response, 'the confirmation')
  return {
    invoiceNumber: text(body['invoiceNumber'], 'invoiceNumber'),
    sequence: whole(body['sequence'], 'sequence'),
  }
}

/**
 * `POST /cash-sessions/count`. `POS-004`'s reveal: the blind count is taken, and only then does the
 * till learn what the drawer should have held.
 */
export function toCashCountResult(response: unknown): CashCountResult {
  const body = record(response, 'the count')
  const status = text(body['status'], 'status')
  const balanced = body['balanced']
  if (typeof balanced !== 'boolean') throw new WriteResultMismatch('balanced')
  const report = record(body['zReport'], 'zReport')
  const movements = report['movements']
  if (!Array.isArray(movements)) throw new WriteResultMismatch('zReport.movements')
  return {
    status: status as CashCountResult['status'],
    expected: money(body['expected'], 'expected'),
    counted: money(body['counted'], 'counted'),
    variance: money(body['variance'], 'variance'),
    balanced,
    zReport: {
      openingFloat: money(report['openingFloat'], 'zReport.openingFloat'),
      cashTaken: money(report['cashTaken'], 'zReport.cashTaken'),
      payIns: money(report['payIns'], 'zReport.payIns'),
      payOuts: money(report['payOuts'], 'zReport.payOuts'),
      movements: movements.map((movement, index) => {
        const shape = record(movement, `zReport.movements[${String(index)}]`)
        return {
          amount: money(shape['amount'], `zReport.movements[${String(index)}].amount`),
          reason: text(shape['reason'], `zReport.movements[${String(index)}].reason`),
        }
      }),
    },
  }
}

/**
 * The id off any of the four loan-frame writes, which all answer with the whole `LoanFrameView`.
 *
 * **Only the id, deliberately.** The screen reads the loan back through `GET
 * /loan-frames/:loanFrameId` rather than rendering the answer, so there is one mapper for the loan
 * shape and it lives in `reads.ts` with the read it was written for. What this is for is the one
 * thing a read cannot supply: the id of a loan that has just been created and is in no list. `GET
 * /loan-frames/overdue` filters to overdue, so a frame lent out this morning is in no collection
 * the counter can ask for, and without this the shop would record a loan and watch it disappear,
 * which invites recording it twice.
 */
export function toLoanFrameId(response: unknown): string {
  const body = record(response, 'the loan frame')
  return text(body['id'], 'id')
}
